import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Box, Drawer, List,
  useMediaQuery, useTheme, IconButton, Divider,
} from '@mui/material';
import {
  Dashboard, QrCodeScanner, CalendarMonth, AccountBalance,
  BarChart, People, EventAvailable, Menu as MenuIcon, Store, Loyalty, Language, LocalOffer, AutoAwesome,
} from '@mui/icons-material';
import { useState } from 'react';
import { colors } from '../../theme/theme';
import ChatWidget from '../shared/ChatWidget';
import NavListItem from '../shared/NavListItem';
import NotificationsBell from '../notifications/NotificationsBell';
import { useTranslation } from 'react-i18next';

const DRAWER_WIDTH = 260;

export default function PartnerLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();

  const menuItems = [
    { label: t('partner.dashboard'), icon: <Dashboard />, path: '/partner' },
    { label: t('partner.scan_qr'), icon: <QrCodeScanner />, path: '/partner/scan' },
    { label: t('partner.events'), icon: <CalendarMonth />, path: '/partner/events' },
    { label: t('partner.venue_profile'), icon: <Store />, path: '/partner/venue' },
    { label: t('partner.settlements'), icon: <AccountBalance />, path: '/partner/settlements' },
    { label: t('partner.analytics'), icon: <BarChart />, path: '/partner/analytics' },
    { label: t('partner.loyalty'), icon: <Loyalty />, path: '/partner/loyalty' },
    { label: t('partner.website'), icon: <Language />, path: '/partner/website' },
    { label: t('partner.crm'), icon: <People />, path: '/partner/crm' },
    { label: t('partner.offers'), icon: <LocalOffer />, path: '/partner/offers' },
    { label: 'Automations', icon: <AutoAwesome />, path: '/partner/automations' },
    { label: t('partner.staff'), icon: <People />, path: '/partner/staff' },
    { label: t('partner.availability'), icon: <EventAvailable />, path: '/partner/availability' },
  ];

  const drawer = (
    <Box sx={{ pt: 2 }}>
      {/* DS exemption: ☕ is brand-whimsy in a non-functional lockup
          (DESIGN_SYSTEM.md §Decorative emoji policy → Exemption #3). */}
      <Typography variant="h6" sx={{ px: 2, pb: 2, fontWeight: 700, color: colors.primary.main }}>
        ☕ Partner Portal
      </Typography>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <NavListItem
            key={item.path}
            to={item.path}
            label={item.label}
            icon={item.icon}
            role="partner"
            active={location.pathname === item.path}
            onNavigate={() => { navigate(item.path); setMobileOpen(false); }}
          />
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      {isMobile ? (
        <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)} sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}>
          {drawer}
        </Drawer>
      ) : (
        <Drawer variant="permanent" sx={{ width: DRAWER_WIDTH, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, borderRight: `1px solid ${colors.neutral[200]}` } }}>
          {drawer}
        </Drawer>
      )}

      {/* Main */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {isMobile && (
          <AppBar position="sticky" elevation={0}>
            <Toolbar>
              <IconButton onClick={() => setMobileOpen(true)} edge="start"><MenuIcon /></IconButton>
              <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700, color: colors.primary.main }}>Partner Portal</Typography>
              <NotificationsBell />
            </Toolbar>
          </AppBar>
        )}
        <Box sx={{ flex: 1, p: { xs: 2, md: 3 }, bgcolor: 'background.default', position: 'relative' }}>
          {/* Bell pinned top-right on desktop (no app bar there). */}
          {!isMobile && (
            <Box sx={{ position: 'absolute', top: 12, right: 16, zIndex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <NotificationsBell />
            </Box>
          )}
          <Outlet />
        </Box>
      </Box>

      {/* Support Chat Widget */}
      <ChatWidget />
    </Box>
  );
}
